import { useState, useRef, useEffect } from "react"
import {
    FiFolder,
    FiCheck,
    FiEdit2,
    FiLock,
    FiMoreVertical,
    FiUnlock,
    FiSettings,
    FiTrash,
} from "react-icons/fi"
import { LuLoaderCircle } from "react-icons/lu"
import { MdDriveFileMove } from "react-icons/md"

import { useDragPreview } from "../ui/DragPreview"
import { ASSET_DRAG_MIME, FOLDER_DRAG_MIME } from "./constants"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/shadcn/dropdown-menu"

// Folder card. Behaves like an asset card: draggable (carries selected items),
// supports drop targets (assets/folders dropped on it move into the folder),
// and exposes a hover toolbar for rename / privacy / delete.
export default function FolderCard({
    folder,
    onOpen,
    onRename,
    onInlineRename,
    onDelete,
    onMove,
    onToggleVisibility,
    onAssetDrop,
    busy = false,
    selectable = false,
    selected = false,
    onToggleSelect,
    selectedFolderIds = [],
    selectedAssetIds = [],
    draggingFolderIds = [],
    onBeginFolderDrag,
    onItemDragStart,
}) {
    const [loading, setLoading] = useState(false)
    const [isDragOver, setIsDragOver] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [editingName, setEditingName] = useState(false)
    const [savingName, setSavingName] = useState(false)
    const [nameDraft, setNameDraft] = useState(folder.name)
    const nameInputRef = useRef(null)
    // When the rename is committed/cancelled via the keyboard we also trigger a
    // blur; this flag prevents the blur handler from committing a second time.
    const skipBlurRef = useRef(false)
    const showLoader = loading || busy || savingName
    const { showDragPreview } = useDragPreview()

    useEffect(() => {
        if (editingName && nameInputRef.current) {
            nameInputRef.current.focus()
            nameInputRef.current.select()
        }
    }, [editingName])

    const startRename = () => {
        setNameDraft(folder.name)
        setEditingName(true)
    }

    const commitRename = async () => {
        const trimmed = nameDraft.trim()
        if (!trimmed || trimmed === folder.name) {
            setEditingName(false)
            return
        }
        // Keep the input visible (disabled, with a spinner) until the rename
        // round-trip + refetch completes, so the old name never flashes back.
        setSavingName(true)
        try {
            if (onInlineRename) await onInlineRename(folder, trimmed)
        } finally {
            setSavingName(false)
            setEditingName(false)
        }
    }

    const cancelRename = () => {
        setEditingName(false)
        setNameDraft(folder.name)
    }

    const handleNameKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            skipBlurRef.current = true
            commitRename()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            skipBlurRef.current = true
            cancelRename()
        }
    }

    const handleNameBlur = () => {
        if (skipBlurRef.current) {
            skipBlurRef.current = false
            return
        }
        if (savingName) return
        commitRename()
    }

    // True when this card represents one of the folders currently being dragged.
    // We disable hover state and block drops onto self to prevent moving a
    // folder into itself.
    const isDragSource = draggingFolderIds.includes(folder._id)

    const handleDragStart = (e) => {
        const folderIds = selected && selectedFolderIds.length > 0 ? selectedFolderIds : [folder._id]
        const assetIds = selected ? selectedAssetIds : []
        const totalCount = folderIds.length + assetIds.length

        showDragPreview(e, {
            thumbnailUrl: null,
            name: folder.name,
            mediaType: 'folder',
            count: totalCount,
        })

        try {
            e.dataTransfer.setData(FOLDER_DRAG_MIME, JSON.stringify(folderIds))
            if (assetIds.length > 0) {
                e.dataTransfer.setData(ASSET_DRAG_MIME, JSON.stringify(assetIds))
            }
        } catch {}
        e.dataTransfer.effectAllowed = 'move'
        onBeginFolderDrag?.(folderIds)
        onItemDragStart?.({ assetIds, folderIds })
    }

    const handleDragOver = (e) => {
        const types = e.dataTransfer?.types
        if (!types) return
        const arr = Array.from(types)
        const acceptsAsset = arr.includes(ASSET_DRAG_MIME)
        const acceptsFolder = arr.includes(FOLDER_DRAG_MIME)
        if (!acceptsAsset && !acceptsFolder) return

        // Block any drop onto the folder while it's the drag source. We still
        // call preventDefault so the browser shows the no-drop cursor instead
        // of falling through to the editor's file-upload zone.
        if (isDragSource) {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'none'
            return
        }

        e.preventDefault()
        setIsDragOver(true)
        e.dataTransfer.dropEffect = 'move'
    }

    const handleDragLeave = () => {
        setIsDragOver(false)
    }

    const handleDrop = async (e) => {
        if (isDragSource) return
        const assetData = e.dataTransfer.getData(ASSET_DRAG_MIME)
        const folderData = e.dataTransfer.getData(FOLDER_DRAG_MIME)
        if (!assetData && !folderData) return
        e.preventDefault()
        setIsDragOver(false)
        try {
            let folderIds = []
            if (folderData) {
                const parsed = JSON.parse(folderData)
                folderIds = Array.isArray(parsed) ? parsed : [parsed]
                folderIds = folderIds.filter(id => id !== folder._id)
            }
            let assetIds = []
            if (assetData) {
                const parsed = JSON.parse(assetData)
                assetIds = Array.isArray(parsed) ? parsed : []
            }
            if (assetIds.length === 0 && folderIds.length === 0) return
            await onAssetDrop?.({ folder, assetIds, folderIds })
        } catch (err) {
            console.error('Bad drag payload', err)
        }
    }

    const handleDelete = async () => {
        setLoading(true)
        await onDelete?.(folder)
        setLoading(false)
    }

    const cardClickHandler = (e) => {
        if (selectable) {
            e.stopPropagation()
            onToggleSelect?.(folder)
        } else {
            onOpen(folder)
        }
    }

    return (
        <div
            className={`flex flex-col gap-1 relative group ${isDragSource ? 'opacity-40 cursor-not-allowed' : ''}`}
            draggable={!editingName}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <div
                className={`flex flex-col items-center justify-center h-full relative aspect-video bg-card rounded-md overflow-hidden border border-secondary/60 cursor-pointer hover:bg-secondary/60 ${selected ? 'ring-2 ring-primary' : (selectable ? 'outline-2 outline-dashed outline-secondary-foreground/40' : '')} ${isDragOver && !isDragSource ? 'ring-2 ring-primary' : ''}`}
                onDoubleClick={() => onOpen(folder)}
                onClick={cardClickHandler}
            >
                <FiFolder className='w-1/4 h-1/4 text-secondary-foreground/40' />
                {selectable && selected && (
                    <div className='absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-0.5'>
                        <FiCheck className='w-3 h-3' />
                    </div>
                )}
                {folder.visibility === 'private' && (
                    <div className='absolute top-1 left-1 flex items-center gap-1 text-[10px] bg-background/80 text-secondary-foreground rounded-sm px-1 py-0.5'>
                        <FiLock className='w-3 h-3' /> Private
                    </div>
                )}
                {!selectable && <div
                    className='absolute bottom-0 right-0 p-1 hidden group-hover:flex data-[menu-open=true]:flex'
                    data-menu-open={menuOpen}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                >
                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                        <DropdownMenuTrigger asChild>
                            <button
                                type='button'
                                title='Actions'
                                className='p-1.5 flex items-center justify-center bg-card rounded-md border text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer'
                                onClick={(e) => e.stopPropagation()}
                            >
                                <FiMoreVertical className='w-3.5 h-3.5' />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end' side='bottom'>
                            <DropdownMenuItem className='text-xs p-2' onClick={() => onRename?.(folder)}>
                                <FiSettings className='w-4 h-4' />
                                Settings
                            </DropdownMenuItem>
                            <DropdownMenuItem className='text-xs p-2' onClick={() => onToggleVisibility?.(folder)}>
                                {folder.visibility === 'private'
                                    ? <FiUnlock className='w-4 h-4' />
                                    : <FiLock className='w-4 h-4' />}
                                {folder.visibility === 'private' ? 'Make public' : 'Make private'}
                            </DropdownMenuItem>
                            {onMove && (
                                <DropdownMenuItem className='text-xs p-2' onClick={() => onMove?.(folder)}>
                                    <MdDriveFileMove className='w-4 h-4' />
                                    Move
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className='text-xs p-2' variant='destructive' onClick={handleDelete}>
                                <FiTrash className='w-4 h-4' />
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>}
                {showLoader && (
                    <div className='absolute top-0 left-0 w-full h-full bg-black/60 text-white flex items-center justify-center'>
                        <LuLoaderCircle className='w-6 h-6 animate-spin' />
                    </div>
                )}
            </div>
            <div className='flex flex-col items-start px-1 w-full min-w-0'>
                <span className='w-full truncate text-[10px] text-secondary-foreground/50'>{folder.user?.email}</span>
                {editingName ? (
                    <textarea
                        ref={nameInputRef}
                        rows={1}
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onBlur={handleNameBlur}
                        onKeyDown={handleNameKeyDown}
                        onClick={(e) => e.stopPropagation()}
                        disabled={savingName}
                        className='w-full box-border text-xs leading-tight bg-card rounded-sm px-0 py-0 m-0 border-0 text-secondary-foreground outline-none ring-1 ring-primary disabled:opacity-70 resize-none break-words field-sizing-content'
                    />
                ) : (
                    <div className='relative w-full min-w-0 group/name'>
                        <span
                            className='block w-full text-xs leading-tight text-secondary-foreground break-words pr-4 cursor-text'
                            onDoubleClick={startRename}
                            title='Double-click to rename'
                        >
                            {folder.name}
                        </span>
                        <button
                            type='button'
                            onClick={startRename}
                            title='Rename'
                            className='absolute right-0 top-0 hidden group-hover/name:flex items-center justify-center p-0.5 rounded-sm bg-card/80 text-secondary-foreground/70 hover:text-secondary-foreground cursor-pointer'
                        >
                            <FiEdit2 className='w-3 h-3' />
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
